import axios from 'axios';

export type SavedCustomFieldFile = {
  file_path?: string | null;
  file_paths?: string | null;
  names: string[];
};

type UploadableField = {
  id: number;
  field_type: string;
  label?: string;
  name?: string;
};

const fileBasename = (path: string): string => {
  const normalized = String(path || '').replace(/\\/g, '/');
  const parts = normalized.split('/');
  return parts[parts.length - 1] || normalized;
};

export const hasSavedCustomFieldFile = (saved?: SavedCustomFieldFile | null): boolean => {
  if (!saved) return false;
  if (saved.names.length > 0) return true;
  if (saved.file_path && String(saved.file_path).trim()) return true;
  if (saved.file_paths && String(saved.file_paths).trim()) return true;
  return false;
};

export const savedCustomFieldFileLabel = (saved: SavedCustomFieldFile): string => {
  if (saved.names.length === 1) return `Arquivo salvo: ${saved.names[0]}`;
  if (saved.names.length > 1) {
    return `${saved.names.length} arquivo(s) salvo(s): ${saved.names.join(', ')}`;
  }
  return 'Arquivo(s) salvo(s) no rascunho';
};

export const parseSavedCustomFieldFilesFromApi = (cf: {
  file_path?: string | null;
  file_paths?: string | null;
  text_value?: string | null;
}): SavedCustomFieldFile | null => {
  const names: string[] = [];
  const filePath = cf.file_path ? String(cf.file_path).trim() : '';
  const filePathsRaw = cf.file_paths ? String(cf.file_paths).trim() : '';

  if (filePath) {
    const displayName = String(cf.text_value || '').trim() || fileBasename(filePath);
    names.push(displayName);
    return {
      file_path: filePath,
      file_paths: filePathsRaw || null,
      names,
    };
  }

  if (!filePathsRaw) return null;

  try {
    const parsed = JSON.parse(filePathsRaw);
    if (Array.isArray(parsed)) {
      for (const item of parsed) {
        if (typeof item === 'string' && item.trim()) {
          names.push(fileBasename(item));
        } else if (item && typeof item === 'object') {
          const name = String(item.name || '').trim();
          const path = String(item.path || '').trim();
          if (name) names.push(name);
          else if (path) names.push(fileBasename(path));
        }
      }
    }
  } catch {
    names.push(fileBasename(filePathsRaw));
  }

  if (names.length === 0) return null;
  return {
    file_path: null,
    file_paths: filePathsRaw,
    names,
  };
};

export const uploadPurchaseCustomFieldFiles = async (params: {
  apiUrl: string;
  purchaseRequestId: number;
  customFields: UploadableField[];
  customFieldFiles: { [fieldId: number]: File | File[] | null | undefined };
  platformDocumentMode?: { [fieldId: number]: string };
}): Promise<{ uploadedFieldIds: number[]; errors: string[]; savedByFieldId: { [fieldId: number]: SavedCustomFieldFile } }> => {
  const {
    apiUrl,
    purchaseRequestId,
    customFields,
    customFieldFiles,
    platformDocumentMode = {},
  } = params;

  const uploadedFieldIds: number[] = [];
  const errors: string[] = [];
  const savedByFieldId: { [fieldId: number]: SavedCustomFieldFile } = {};

  const fieldsToUpload = customFields.filter((field) => {
    const fieldFile = customFieldFiles[field.id];
    if (!fieldFile) return false;
    const mode = platformDocumentMode[field.id] || 'upload';
    if (field.field_type === 'platform_document') {
      return mode === 'upload';
    }
    return field.field_type === 'file' || field.field_type === 'files';
  });

  for (const field of fieldsToUpload) {
    const fieldFile = customFieldFiles[field.id];
    if (!fieldFile) continue;
    const mode = platformDocumentMode[field.id] || 'upload';
    const fieldLabel = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;

    try {
      if (field.field_type === 'file' || (field.field_type === 'platform_document' && mode === 'upload')) {
        const formData = new FormData();
        formData.append('file', fieldFile as File);
        const uploadResponse = await axios.post(
          `${apiUrl}/api/purchases/${purchaseRequestId}/custom-fields/${field.id}/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const parsed = parseSavedCustomFieldFilesFromApi(uploadResponse.data || {});
        if (parsed) {
          savedByFieldId[field.id] = parsed;
        } else {
          savedByFieldId[field.id] = {
            file_path: uploadResponse.data?.file_path || null,
            file_paths: null,
            names: [(fieldFile as File).name],
          };
        }
        uploadedFieldIds.push(field.id);
      } else if (field.field_type === 'files') {
        const formData = new FormData();
        const files = Array.isArray(fieldFile) ? fieldFile : [fieldFile as File];
        files.forEach((f) => formData.append('files', f));
        const uploadResponse = await axios.post(
          `${apiUrl}/api/purchases/${purchaseRequestId}/custom-fields/${field.id}/upload-multiple`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        const parsed = parseSavedCustomFieldFilesFromApi(uploadResponse.data || {});
        if (parsed) {
          savedByFieldId[field.id] = parsed;
        } else {
          savedByFieldId[field.id] = {
            file_path: null,
            file_paths: uploadResponse.data?.file_paths || null,
            names: files.map((f) => f.name),
          };
        }
        uploadedFieldIds.push(field.id);
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const detailText =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((item) => item?.msg || String(item)).join('; ')
            : error?.message || 'erro desconhecido';
      errors.push(`Campo "${fieldLabel}": ${detailText}`);
    }
  }

  return { uploadedFieldIds, errors, savedByFieldId };
};
